import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API } from '../config/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'same-origin',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        // Store the access token if returned (for OAuth sessions)
        if (userData.access_token && !token) {
          localStorage.setItem('token', userData.access_token);
          setToken(userData.access_token);
        }
      } else {
        setUser(null);
        localStorage.removeItem('token');
        setToken(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = useCallback(async () => {
    try {
      const currentToken = localStorage.getItem('token');
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'same-origin',
        headers: currentToken ? { 'Authorization': `Bearer ${currentToken}` } : {}
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return userData;
      }
    } catch (error) {
      console.error('User refresh failed:', error);
    }
    return null;
  }, []);

  const login = async (email, password) => {
    let responseData = null;
    let responseOk = false;
    let responseStatus = 0;
    
    try {
      console.log('[Auth] Login attempt for:', email);
      console.log('[Auth] API endpoint:', `${API}/auth/login`);
      
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      responseOk = response.ok;
      responseStatus = response.status;
      console.log('[Auth] Response status:', response.status, responseOk ? 'OK' : 'FAILED');
      
      // Parse response as text first, then JSON (more reliable)
      try {
        const text = await response.text();
        responseData = JSON.parse(text);
        console.log('[Auth] Response parsed successfully');
      } catch (parseError) {
        console.error('[Auth] Failed to parse response:', parseError);
        // Provide meaningful error based on status code when parsing fails
        if (responseStatus === 401) {
          throw new Error('Invalid email or password. Please try again.');
        } else if (responseStatus === 403) {
          throw new Error('Account access denied.');
        } else if (responseStatus === 500) {
          throw new Error('Server error. Please try again later.');
        }
        throw new Error(`Server error (${responseStatus}). Please try again.`);
      }
      
      if (!responseOk) {
        console.error('[Auth] Login failed with response:', responseData);
        
        // Handle structured error responses with code and message
        const detail = responseData?.detail;
        if (typeof detail === 'object' && detail?.message) {
          throw new Error(detail.message);
        }
        
        // Handle CORS errors (usually show as network errors)
        if (response.status === 0) {
          throw new Error('Network error. Please check your connection and try again.');
        }
        
        // Handle common HTTP errors
        if (response.status === 401) {
          throw new Error(detail || 'Invalid email or password');
        }
        if (response.status === 403) {
          throw new Error(detail || 'Account access denied');
        }
        if (response.status === 500) {
          throw new Error('Server error. Please try again later.');
        }
        
        throw new Error(detail || responseData?.message || `Login failed (${response.status})`);
      }
      
      console.log('[Auth] Login successful for user:', responseData.user?.email);
      
      // Check for force password reset (temp password accounts)
      if (responseData.force_password_reset) {
        return responseData; // Let the LoginPage handle the redirect
      }

      // Direct login - 2FA has been removed
      localStorage.setItem('token', responseData.access_token);
      setToken(responseData.access_token);
      setUser(responseData.user);
      return responseData;
    } catch (error) {
      console.error('[Auth] Login error:', error);
      
      // Clean up error message for user display
      const message = error.message || 'Login failed';
      
      // Handle network/CORS errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      
      // Filter out technical errors and show clean message
      if (message.includes('clone') || message.includes('body') || message.includes('arrayBuffer')) {
        throw new Error('Login failed - please try again');
      }
      
      throw new Error(message);
    }
  };

  // Direct login with token (used for OAuth callbacks)
  const loginWithToken = (userData, accessToken) => {
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    setUser(userData);
  };

  const register = async (email, password, name, role = 'listener', agreedToGuidelines = false, termsVersion = '1.0.0', phoneNumber = null, orgData = null, handle = null, neighborhoodId = null) => {
    let responseData = null;
    let responseOk = false;
    
    try {
      console.log('[Auth] Registration attempt for:', email, 'role:', role);
      console.log('[Auth] API endpoint:', `${API}/auth/register`);
      
      // Build registration payload
      const payload = { 
        email, 
        password, 
        name, 
        role, 
        handle: handle || null,  // Handle for ALL users
        agreed_to_guidelines: agreedToGuidelines,
        terms_version: termsVersion,
        terms_accepted_at: new Date().toISOString(),
        phone_number: phoneNumber,
        neighborhood_id: neighborhoodId
      };
      
      // Add organization-specific fields if role is organization
      if (role === 'organization' && orgData) {
        payload.handle = orgData.handle || handle;
        payload.org_type = orgData.org_type;
        payload.mission = orgData.mission;
        payload.city = orgData.city;
        payload.state = orgData.state;
        payload.neighborhood = orgData.neighborhood;
        payload.website = orgData.website;
      }
      
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload)
      });
      
      responseOk = response.ok;
      console.log('[Auth] Registration response status:', response.status, responseOk ? 'OK' : 'FAILED');
      
      // Safe response parsing - always try text first, then parse JSON
      let responseText = '';
      try {
        responseText = await response.text();
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        console.error('[Auth] Response parse error:', parseError, 'Raw response:', responseText?.substring(0, 500));
        throw new Error(`Server error (${response.status}). Please try again.`);
      }
      
      if (!responseOk) {
        console.error('[Auth] Registration failed with response:', responseData);
        
        // Handle CORS errors
        if (response.status === 0) {
          throw new Error('Network error. Please check your connection and try again.');
        }
        
        // Extract error message from various response formats
        let errorMessage = 'Registration failed';
        let requestId = null;
        
        if (responseData?.detail) {
          if (typeof responseData.detail === 'string') {
            errorMessage = responseData.detail;
          } else if (responseData.detail.message) {
            errorMessage = responseData.detail.message;
            requestId = responseData.detail.requestId;
          }
        } else if (responseData?.message) {
          errorMessage = responseData.message;
        }
        
        // Append request ID for debugging if available
        if (requestId) {
          errorMessage = `${errorMessage} (Ref: ${requestId})`;
        }
        
        throw new Error(errorMessage);
      }
      
      console.log('[Auth] Registration successful for:', email);
      
      localStorage.setItem('token', responseData.access_token);
      setToken(responseData.access_token);
      setUser(responseData.user);
      return responseData;
    } catch (error) {
      console.error('[Auth] Registration error:', error);
      
      // Handle network/CORS errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      
      const message = error.message || 'Registration failed';
      throw new Error(message);
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'same-origin'
      });
    } catch (e) {
      console.error('Logout error:', e);
    }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const processOAuthSession = async (sessionId) => {
    const response = await fetch(`${API}/auth/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ session_id: sessionId })
    });
    
    if (!response.ok) {
      throw new Error('OAuth session processing failed');
    }
    
    const data = await response.json();
    
    // Direct login - 2FA has been removed
    setUser(data.user);
    
    // Save the access token to localStorage for API calls
    if (data.access_token) {
      localStorage.setItem('token', data.access_token);
      setToken(data.access_token);
    }
    
    return { user: data.user, is_new_user: data.is_new_user };
  };

  const getAuthHeaders = () => {
    // Check both state and localStorage for token (fallback for stale state)
    const currentToken = token || localStorage.getItem('token');
    return currentToken ? { 'Authorization': `Bearer ${currentToken}` } : {};
  };

  /**
   * Authenticated fetch wrapper with automatic token refresh on 401
   * Use this for all authenticated API calls to handle session expiry gracefully
   */
  const authenticatedFetch = useCallback(async (url, options = {}) => {
    const currentToken = token || localStorage.getItem('token');
    
    if (!currentToken) {
      throw new Error('Not authenticated');
    }
    
    // Merge auth headers with provided headers
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${currentToken}`
    };
    
    const fetchOptions = {
      ...options,
      headers,
      credentials: 'same-origin'
    };
    
    let response = await fetch(url, fetchOptions);
    
    // If we get a 401, try to refresh the session
    if (response.status === 401) {
      console.log('[Auth] Token expired, attempting refresh...');
      
      // Try to refresh the session via /auth/me
      const refreshResponse = await fetch(`${API}/auth/me`, {
        credentials: 'same-origin',
        headers: currentToken ? { 'Authorization': `Bearer ${currentToken}` } : {}
      });
      
      if (refreshResponse.ok) {
        const userData = await refreshResponse.json();
        
        // If we got a new token, update state and retry
        if (userData.access_token) {
          localStorage.setItem('token', userData.access_token);
          setToken(userData.access_token);
          setUser(userData);
          
          // Retry the original request with new token
          const retryHeaders = {
            ...options.headers,
            'Authorization': `Bearer ${userData.access_token}`
          };
          
          response = await fetch(url, { ...fetchOptions, headers: retryHeaders });
          console.log('[Auth] Token refreshed, retried request');
        }
      } else {
        // Refresh failed - clear auth state
        console.log('[Auth] Session refresh failed, logging out');
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        throw new Error('Session expired - please log in again');
      }
    }
    
    return response;
  }, [token]);

  // Check if artist needs to subscribe
  const requiresSubscription = user?.is_artist && !user?.subscription_active && !user?.is_owner;
  
  // Check if user has active artist subscription
  const hasActiveSubscription = user?.subscription_active === true || user?.is_owner === true;
  
  // Check if user is platform owner
  const isOwner = user?.is_owner === true || user?.role === 'owner';
  
  // HARDCODED PLATFORM OWNER - Only this email can have full owner access
  // This cannot be changed without code deployment
  const PLATFORM_OWNER_EMAIL = 'itzchopbusiness1@gmail.com';
  const PLATFORM_OWNER_HANDLE = 'chopjohnson';
  
  // Check if current user is THE platform owner (not just any owner role)
  const isPlatformOwner = Boolean(
    user && 
    (user.email?.toLowerCase() === PLATFORM_OWNER_EMAIL.toLowerCase() ||
     user.is_platform_super_admin === true ||
     (user.handle?.toLowerCase() === PLATFORM_OWNER_HANDLE.toLowerCase() && user.is_owner))
  );

  // Get role-based dashboard path
  const getDashboardPath = () => {
    const role = user?.role;
    if (!role) return '/dashboard';
    if (role === 'owner') return '/artist/dashboard';
    return `/${role}/dashboard`;
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      token,
      login,
      loginWithToken,
      register,
      logout,
      processOAuthSession,
      getAuthHeaders,
      authenticatedFetch,
      refreshUser,
      isAuthenticated: !!user,
      isArtist: user?.is_artist || user?.role === 'artist' || user?.role === 'owner',
      isAdmin: user?.role === 'admin' || user?.role === 'owner',
      isOwner,
      isPlatformOwner,
      requiresSubscription,
      hasActiveSubscription,
      artistTier: user?.is_owner ? 'label' : user?.artist_tier,
      bypassBilling: user?.bypass_billing || user?.is_owner,
      userRole: user?.role || 'listener',
      getDashboardPath,
      needsProfileCompletion: !!user && !user.neighborhood_id && !user.neighborhood
    }}>
      {children}
    </AuthContext.Provider>
  );
};
